<script>
import modalUploadImage from "../modals/modal-upload-image";
import { ROUTES } from "../../utils/constants";

// Fallback only — the server reports the canonical URL via defaultEmailLogoUri
const ASHORE_DEFAULT_LOGO = "https://high-seas.s3.amazonaws.com/email/ashore-header.jpg";
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export default {
  name: "emailBranding",
  components: {
    modalUploadImage,
  },
  data() {
    return {
      loaded: false,
      saving: false,
      // Server state
      emailLogoUri: "",
      accountLogoUri: "",
      canCustomizeLogo: false,
      defaultButtonBackgroundColor: "#d82630",
      defaultButtonTextColor: "#ffffff",
      defaultEmailLogoUri: ASHORE_DEFAULT_LOGO,
      // Editable state
      hideEmailLogo: false,
      showSubjectHeader: true,
      showReviewButton: true,
      buttonBackgroundColor: "#d82630",
      buttonTextColor: "#ffffff",
    };
  },
  computed: {
    hasCustomLogo() {
      return !!this.emailLogoUri;
    },
    // What the email will actually show in its header
    previewLogo() {
      if (this.hideEmailLogo) return "";
      if (this.emailLogoUri) return this.emailLogoUri;
      return this.accountLogoUri || this.defaultEmailLogoUri;
    },
    logoStatusNote() {
      if (this.hideEmailLogo) return "The logo is hidden — emails are sent without one.";
      if (this.hasCustomLogo) return "Using your custom email logo.";
      if (this.accountLogoUri) return "Using your account logo (the default).";
      return "Using the Ashore logo. Upload your account logo or a custom email logo to replace it.";
    },
  },
  async mounted() {
    const self = this;
    await this.loadBranding();
    this.$root.$on("account::emaillogo::updated", async function (url) {
      if (url) {
        self.emailLogoUri = url;
        self.hideEmailLogo = false;
      }
    });
  },
  methods: {
    async loadBranding() {
      const branding = await this.$A.AccountService.GetEmailBranding();
      if (branding) {
        this.applyServerState(branding);
      }
      this.loaded = true;
    },
    applyServerState(branding) {
      this.emailLogoUri = branding.emailLogoUri || "";
      this.accountLogoUri = branding.accountLogoUri || "";
      this.canCustomizeLogo = !!branding.canCustomizeLogo;
      this.defaultButtonBackgroundColor = branding.defaultButtonBackgroundColor || "#d82630";
      this.defaultButtonTextColor = branding.defaultButtonTextColor || "#ffffff";
      this.defaultEmailLogoUri = branding.defaultEmailLogoUri || ASHORE_DEFAULT_LOGO;
      this.hideEmailLogo = !!branding.hideEmailLogo;
      this.showSubjectHeader = !branding.hideSubjectHeader;
      this.showReviewButton = !branding.hideReviewButton;
      this.buttonBackgroundColor = branding.buttonBackgroundColor || this.defaultButtonBackgroundColor;
      this.buttonTextColor = branding.buttonTextColor || this.defaultButtonTextColor;
    },
    showUploadLogoModal() {
      if (!this.canCustomizeLogo) return;
      this.$refs.uploadEmailLogoModal.openModal();
    },
    async resetLogo() {
      const ok = await this.$A.AccountService.ResetEmailLogoImage();
      if (ok) {
        this.emailLogoUri = "";
        this.$A.AlertUser("Email Logo Reset To Your Account Logo", "success");
      }
    },
    async save() {
      if (this.saving) return;
      if (!HEX_COLOR.test(this.buttonBackgroundColor) || !HEX_COLOR.test(this.buttonTextColor)) {
        this.$A.AlertUser("Button colors must be hex values like #d82630.", "warning");
        return;
      }
      this.saving = true;
      const payload = {
        hideSubjectHeader: !this.showSubjectHeader,
        hideReviewButton: !this.showReviewButton,
        // Empty string means "reset to default" so new defaults apply automatically;
        // omitting a color would leave the stored value untouched
        buttonBackgroundColor:
          this.buttonBackgroundColor.toLowerCase() === this.defaultButtonBackgroundColor.toLowerCase()
            ? ""
            : this.buttonBackgroundColor,
        buttonTextColor:
          this.buttonTextColor.toLowerCase() === this.defaultButtonTextColor.toLowerCase()
            ? ""
            : this.buttonTextColor,
      };
      if (this.canCustomizeLogo) {
        payload.hideEmailLogo = this.hideEmailLogo;
      }
      const branding = await this.$A.AccountService.SaveEmailBranding(payload);
      if (branding) {
        this.applyServerState(branding);
      }
      this.saving = false;
    },
    resetButtonColors() {
      this.buttonBackgroundColor = this.defaultButtonBackgroundColor;
      this.buttonTextColor = this.defaultButtonTextColor;
    },
    navigateToBilling() {
      this.$router.push(ROUTES.BILLING);
    },
  },
};
</script>

<template>
  <div class="settings-card" v-if="loaded">
    <div class="card-head">
      <div class="card-head-title">Email Branding</div>
      <div class="card-head-subtitle">
        Control how the emails Ashore sends on your behalf look to your approvers and contacts.
      </div>
    </div>

    <div class="card-body">
      <!-- LOGO -->
      <div class="branding-section">
        <div class="branding-section-title">Logo</div>
        <div class="branding-logo-row">
          <button
              type="button"
              class="logo-tile"
              :class="{ 'is-locked': !canCustomizeLogo }"
              :title="canCustomizeLogo ? (hasCustomLogo ? 'Change email logo' : 'Upload an email logo') : 'Available on paid plans'"
              @click="showUploadLogoModal"
          >
            <span v-if="previewLogo" class="logo-image" :style="{ backgroundImage: 'url(' + previewLogo + ')' }"></span>
            <span v-else class="logo-hidden-label">No logo</span>
            <span class="logo-overlay" v-if="canCustomizeLogo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              {{ hasCustomLogo ? "Change" : "Upload" }}
            </span>
          </button>

          <div class="branding-logo-side">
            <div class="branding-note">{{ logoStatusNote }}</div>

            <template v-if="canCustomizeLogo">
              <div class="ashore-toggle branding-toggle-row">
                <label class="switch">
                  <input type="checkbox" v-model="hideEmailLogo" />
                  <span class="slider round"></span>
                </label>
                Remove the logo from emails entirely
              </div>
              <div class="branding-logo-actions">
                <a class="secondary-btn" @click="showUploadLogoModal">{{ hasCustomLogo ? "Change Logo" : "Upload Custom Logo" }}</a>
                <a v-show="hasCustomLogo" class="secondary-btn" @click="resetLogo">Use Account Logo</a>
              </div>
            </template>

            <div v-else class="logo-locked-note">
              <span class="locked-badge">Paid</span>
              Changing or removing the email logo is available on paid plans.
              <a class="upgrade-link" @click="navigateToBilling">Upgrade Plan</a>
            </div>
          </div>
        </div>
      </div>

      <!-- LAYOUT -->
      <div class="branding-section">
        <div class="branding-section-title">Layout</div>
        <div class="ashore-toggle branding-toggle-row">
          <label class="switch">
            <input type="checkbox" v-model="showSubjectHeader" />
            <span class="slider round"></span>
          </label>
          Show the subject line as a header inside the email body
        </div>
        <div class="ashore-toggle branding-toggle-row">
          <label class="switch">
            <input type="checkbox" v-model="showReviewButton" />
            <span class="slider round"></span>
          </label>
          Show the review button on proof emails
        </div>
        <div class="branding-note" v-if="!showReviewButton">
          Approvers can still open the proof from the review link included in the email text.
        </div>
      </div>

      <!-- BUTTON COLORS -->
      <div class="branding-section" v-if="showReviewButton">
        <div class="branding-section-title">Review Button Colors</div>
        <div class="color-rows">
          <div class="color-row">
            <label class="color-label" for="emailBtnBg">Background</label>
            <input id="emailBtnBg" type="color" class="color-swatch" v-model="buttonBackgroundColor" />
            <input
                type="text"
                class="color-hex"
                v-model.trim="buttonBackgroundColor"
                maxlength="7"
                spellcheck="false"
            />
          </div>
          <div class="color-row">
            <label class="color-label" for="emailBtnText">Text</label>
            <input id="emailBtnText" type="color" class="color-swatch" v-model="buttonTextColor" />
            <input
                type="text"
                class="color-hex"
                v-model.trim="buttonTextColor"
                maxlength="7"
                spellcheck="false"
            />
          </div>
          <a class="reset-colors-link" @click="resetButtonColors">Reset to defaults</a>
        </div>
        <div class="button-preview" :style="{ backgroundColor: buttonBackgroundColor }">
          <span :style="{ color: buttonTextColor }">Review Now</span>
        </div>
      </div>
    </div>

    <div class="card-foot">
      <a class="primary-btn" :class="{ 'is-disabled': saving }" @click="save">Save Email Branding</a>
    </div>

    <modalUploadImage type="emailLogo" ref="uploadEmailLogoModal" />
  </div>
</template>

<style scoped>
.settings-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
}
.card-head-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.card-head-subtitle {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
}
.card-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
}
.card-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 0 0 12px 12px;
}

.branding-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.branding-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.branding-note {
  font-size: 12.5px;
  color: #6b7280;
}
.branding-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  color: #1f2937;
}

/* Logo */
.branding-logo-row {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.logo-tile {
  position: relative;
  width: 220px;
  height: 120px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 10px;
  background: #fff;
  padding: 12px;
  cursor: pointer;
  overflow: hidden;
  flex-shrink: 0;
}
.logo-tile.is-locked {
  cursor: not-allowed;
  opacity: 0.7;
}
.logo-image {
  display: block;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}
.logo-hidden-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 13px;
  color: #9ca3af;
}
.logo-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(17, 24, 39, 0.45);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 120ms ease;
}
.logo-tile:not(.is-locked):hover .logo-overlay {
  opacity: 1;
}
.branding-logo-side {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.branding-logo-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.logo-locked-note {
  font-size: 13px;
  color: #4b5563;
}
.locked-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  margin-right: 6px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
  font-size: 11px;
  font-weight: 600;
}
.upgrade-link {
  margin-left: 6px;
  color: #019256;
  font-weight: 600;
  cursor: pointer;
}
.upgrade-link:hover {
  color: #017a48;
  text-decoration: underline;
}

/* Colors */
.color-rows {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.color-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.color-label {
  margin: 0;
  font-size: 13px;
  color: #4b5563;
}
.color-swatch {
  width: 34px;
  height: 34px;
  padding: 2px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}
.color-hex {
  width: 92px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-family: monospace;
  color: #1f2937;
}
.reset-colors-link {
  font-size: 12.5px;
  color: #019256;
  font-weight: 600;
  cursor: pointer;
}
.reset-colors-link:hover {
  color: #017a48;
  text-decoration: underline;
}
.button-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 320px;
  padding: 14px 24px;
  border-radius: 8px;
}
.button-preview span {
  font-size: 15px;
  font-weight: 700;
}

/* Buttons */
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}
.primary-btn.is-disabled {
  opacity: 0.6;
  pointer-events: none;
}
.secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  background: #fff;
  color: #1f2937;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 120ms ease, color 120ms ease;
}
.secondary-btn:hover {
  border-color: #019256;
  color: #019256;
  text-decoration: none;
}

@media (max-width: 575px) {
  .branding-logo-row {
    flex-direction: column;
  }
  .logo-tile {
    width: 100%;
  }
}
</style>
